import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Typography } from 'antd';

import { StyledButton } from '../../components/Button';
import { StyledRow } from '../../components/StyledRow';
import { AddButtonIcon } from '../dictionaries/DictionaryItemsList';
import {
  DictionaryItemsListContainer,
  InputListItem,
  ListLabel,
  StyledListItem,
} from '../dictionaries/itemsList.styles';

import { ConsentType, consentTypeMap, consentTypes } from './Consents.types';
import { ConsentTemplatePreview } from './ConsentTemplatePreview';
import { useAddConsentTemplate } from './useAddConsentTemplate';
import { useGetConsentTemplates } from './useGetConsentTemplates';

type ConsentFormValues = {
  type: ConsentType;
  content: string;
  englishContent: string;
};

export const ConsentTemplateList: React.FC = () => {
  const [form] = Form.useForm<ConsentFormValues>();

  const { data: consentTemplates } = useGetConsentTemplates();
  const { mutate: addConsent, isLoading: isAdding } = useAddConsentTemplate();

  const [selectedConsentId, setSelectedConsentId] = useState<number | null>(null);

  const onSubmit = (values: ConsentFormValues) => {
    if (isAdding) return;

    addConsent(
      { data: values },
      {
        onSuccess: () => {
          form.resetFields();
        },
      }
    );
  };

  return (
    <>
      <ListLabel>Consents</ListLabel>

      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <InputListItem style={{ borderBottom: '1px solid #dbdbdb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Form.Item name="type" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1, margin: 0 }}>
              <Select
                placeholder="Select consent type"
                options={consentTypes.map((consentType) => ({
                  value: consentType,
                  label: consentTypeMap[consentType],
                }))}
              />
            </Form.Item>
            <Form.Item style={{ margin: 0 }}>
              <StyledButton htmlType="submit" type="primary" icon={<AddButtonIcon />} disabled={isAdding}>
                Add
              </StyledButton>
            </Form.Item>
          </div>
          <Form.Item
            name="content"
            rules={[{ required: true, message: 'Required' }]}
            style={{ marginTop: 10, marginBottom: 5 }}
          >
            <Input.TextArea placeholder="Enter consent content in Polish" rows={8} />
          </Form.Item>
          <Form.Item
            name="englishContent"
            rules={[{ required: true, message: 'Required' }]}
            style={{ marginTop: 10, marginBottom: 5 }}
          >
            <Input.TextArea placeholder="Enter consent content in English" rows={8} />
          </Form.Item>
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            The consent content supports the following dynamic values (when available):
          </Typography.Text>
          <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
            <li>
              <Typography.Text type="secondary" style={{ marginLeft: '-5px' }}>
                <Typography.Text code>{'{{CLIENT_NAME}}'}</Typography.Text> - replaced by the client name
              </Typography.Text>
            </li>
            <li>
              <Typography.Text type="secondary" style={{ marginLeft: '-5px' }}>
                <Typography.Text code>{'{{PROJECT_NAME}}'}</Typography.Text> - replaced by the project name
              </Typography.Text>
            </li>
            {/* TODO: Add `POSITION` variable for `PROJECT_PARTICIPATION` consent type */}
          </ul>
        </InputListItem>
      </Form>

      {consentTemplates && (
        <>
          <DictionaryItemsListContainer style={{ marginTop: 20 }}>
            {consentTemplates.map((item) => (
              <StyledListItem key={item.id} style={{ padding: '10px 5px' }}>
                <StyledRow justifyContent="space-between" style={{ alignItems: 'center' }}>
                  <Button
                    type="text"
                    style={{ height: 'auto', margin: 0, textAlign: 'left' }}
                    onClick={() => setSelectedConsentId(item.id)}
                  >
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                      {consentTypeMap[item.type]}
                    </Typography.Title>
                    <Typography.Text type="secondary">Version {item.version}</Typography.Text>
                  </Button>
                </StyledRow>
              </StyledListItem>
            ))}
          </DictionaryItemsListContainer>
          <Modal
            open={!!selectedConsentId}
            onCancel={() => setSelectedConsentId(null)}
            footer={
              <Button type="primary" onClick={() => setSelectedConsentId(null)}>
                Close
              </Button>
            }
          >
            <ConsentTemplatePreview consentTemplate={consentTemplates.find(({ id }) => id === selectedConsentId)} />
          </Modal>
        </>
      )}
    </>
  );
};
